import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles as SparklesIcon, Castle, ArrowRight, Heart } from "lucide-react";
import Sparkles from "../components/Sparkles";
import LangToggle from "../components/LangToggle";
import { fileUrl, getKingdomInfo } from "../lib/api";
import { useLang, t } from "../lib/i18n";

const CASTLE_BG =
  "https://images.unsplash.com/photo-1761236981261-5ad3e4012470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxtYWdpY2FsJTIwZmFpcnklMjB0YWxlJTIwY2FzdGxlJTIwcGluayUyMHNreXxlbnwwfHx8fDE3ODE1OTQ1OTd8MA&ixlib=rb-4.1.0&q=85";

const TE = { fontFamily: "'Tiro Telugu','Noto Serif Telugu','Yatra One','Eczar',serif" };

export default function Welcome() {
  const lang = useLang();
  const [info, setInfo] = useState({ princess_name: "Anagha", princess_image: null });

  useEffect(() => {
    getKingdomInfo().then(setInfo).catch(() => {});
  }, []);

  const princessSrc = info.princess_image
    ? `${fileUrl(info.princess_image)}?v=${Date.now()}`
    : null;

  return (
    <div className="relative min-h-screen flex flex-col overflow-hidden">
      <div
        className="absolute inset-0 -z-10"
        style={{ backgroundImage: `url(${CASTLE_BG})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/45 via-white/15 to-blush/85" />
      <Sparkles count={42} />

      <header className="px-6 sm:px-12 pt-8 flex items-center justify-between relative z-10">
        <div className="flex items-center gap-2 glass rounded-full pl-2 pr-4 py-1.5">
          <Crown className="text-[#B38728]" size={18} />
          <span className="label-eyebrow" style={TE}>{t("royal_court", lang)}</span>
        </div>
        <div className="flex items-center gap-3">
          <LangToggle />
          <Link to="/dance" data-testid="welcome-gallery-link" className="label-eyebrow hover:text-plum transition-colors" style={TE}>
            {t("to_dance", lang)}
          </Link>
        </div>
      </header>

      <main className="relative z-10 flex-1 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center px-6 sm:px-12 lg:px-24 pb-12 pt-10">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-5">
            <Heart size={14} className="text-[#C45A8A]" fill="#FFC4D6" />
            <span className="label-eyebrow" style={TE}>{t("welcome_eyebrow", lang)}</span>
          </div>

          <p className="font-script text-3xl sm:text-4xl text-plum mb-2" style={TE}>{t("welcome_script", lang)}</p>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] font-semibold text-ink text-balance" style={TE}>
            <span className="gold-text">{t("welcome_title", lang)}</span>
            <br />
            <span className="font-script text-3xl sm:text-4xl text-plum">{t("welcome_sub", lang)}</span>
          </h1>

          <div className="ornament-divider my-6 max-w-md">
            <SparklesIcon size={16} />
          </div>

          <p className="text-base sm:text-lg text-ink-soft max-w-xl leading-relaxed" style={TE}>
            {t("welcome_body", lang)}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="enter-kingdom-btn" className="btn-royal group" style={TE}>
              {t("cta_enter", lang)}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/gallery" data-testid="view-gallery-btn" className="btn-ghost-royal" style={TE}>
              {t("cta_gallery", lang)}
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2">
            <Link to="/dance" className="inline-flex items-center gap-2 text-plum hover:text-plum-deep underline underline-offset-4" style={TE}>
              <SparklesIcon size={14} /> {t("cta_dance_floor", lang)} →
            </Link>
            <Link to="/memories" className="inline-flex items-center gap-2 text-plum hover:text-plum-deep underline underline-offset-4" style={TE} data-testid="welcome-memories-link">
              <Heart size={14} className="text-[#C45A8A]" fill="#FFC4D6" />
              {lang === "te" ? "అనఘ జ్ఞాపకాలు" : "Memories Slideshow"} →
            </Link>
          </div>

          <div className="mt-12 flex items-center gap-4">
            <Castle size={20} className="text-[#B38728]" />
            <p className="label-eyebrow" style={TE}>{t("cake_finale", lang)}</p>
          </div>
        </div>

        {princessSrc && (
          <div className="relative mx-auto lg:mx-0 max-w-md w-full">
            <div
              className="absolute -inset-6 rounded-full opacity-50 blur-3xl"
              style={{ background: "radial-gradient(circle, #FCF6BA, transparent 70%)" }}
            />
            <div
              className="relative magical-card p-3 animate-float"
              style={{ animationDuration: "8s" }}
              data-testid="princess-portrait"
            >
              <div className="overflow-hidden bg-blush" style={{ borderRadius: "50% 50% 18px 18px / 28% 28% 18px 18px" }}>
                <img src={princessSrc} alt={`Princess ${info.princess_name}`} className="w-full aspect-square object-cover" />
              </div>
              <div className="text-center pt-4 pb-2">
                <Crown className="mx-auto text-[#B38728]" size={20} />
                <p className="font-script text-4xl text-plum leading-none mt-1" style={TE}>{t("princess_name", lang)}</p>
                <p className="label-eyebrow mt-1" style={TE}>{t("princess_sub", lang)}</p>
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 sm:-right-8 w-20 h-24 sm:w-28 sm:h-32 grid place-items-center rotate-[6deg] animate-float" style={{ animationDuration: "5s" }}>
              <div
                className="w-full h-full grid place-items-center rounded-2xl"
                style={{
                  background: "linear-gradient(160deg, #FCF6BA, #D4AF37 60%, #B38728)",
                  boxShadow: "0 12px 36px rgba(212,175,55,0.5)",
                }}
              >
                <span className="font-display text-6xl sm:text-7xl text-plum-deep font-bold drop-shadow">1</span>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="relative z-10 px-6 sm:px-12 pb-6 flex items-center justify-between text-ink-soft text-xs">
        <span className="label-eyebrow" style={TE}>2026 · {t("welcome_title", lang)}</span>
        <Link to="/director" data-testid="projector-mode-link" className="label-eyebrow hover:text-plum" style={TE}>
          {t("projector", lang)} →
        </Link>
      </footer>
    </div>
  );
}
